import React, { useEffect } from 'react'
import { Params, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { DepositFormValues } from '@pages/Traders-Room/Deposit/DepositFlowLayout/types'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { MasterTransaction } from '@model/MasterTransaction'
import { PaymentProviderType } from '@model/PaymentProviderType'
import {
  TransactionStatus,
  isTransactionStatusAwaitingApproval,
  isTransactionStatusCompleted,
  isTransactionStatusPending,
  isTransactionStatusUnSuccessful,
} from '@model/TransactionStatus'

import { safeLocalStorage, safeSessionStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import {
  isPaymentRedirectTransactionCompletedStatus,
  isPaymentRedirectTransactionFailedStatus,
  isPaymentRedirectTransactionSuccessStatus,
} from './PaymentRedirectTransactionStatusType'

interface DepositRedirectStorageData {
  id: string
  data: MasterTransaction
  providerCategoryId: number
  match: { walletId?: string; tradingAccountId?: string }
  redirectPath?: string
  dashboardContext?: string
}

const PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY = 'provider_deposit_response_transaction'
type RedirectTarget = 'succeed' | 'completed' | 'failed'

const isDepositRedirectStorageData = (
  value?: DepositRedirectStorageData | null
): value is DepositRedirectStorageData => {
  return Boolean(value?.id && value?.data)
}

const getDepositTransactionBasePath = (
  transaction: DepositRedirectStorageData
): string | undefined => {
  const dashboardContext = transaction.dashboardContext || 'traders-room'

  if (transaction.match.walletId) {
    return `/dashboard/${dashboardContext}/deposit/wallet/${transaction.match.walletId}`
  }

  if (transaction.match.tradingAccountId) {
    return `/dashboard/${dashboardContext}/deposit/trading-account/${transaction.match.tradingAccountId}`
  }

  return undefined
}

const getRedirectTarget = (
  statusId: string,
  transactionStateId: number
): RedirectTarget | undefined => {
  if (isTransactionStatusUnSuccessful(transactionStateId)) {
    return 'failed'
  }

  if (isTransactionStatusCompleted(transactionStateId)) {
    return 'succeed'
  }

  if (isPaymentRedirectTransactionFailedStatus(statusId)) {
    return 'failed'
  }

  const isTransactionInProgress =
    isTransactionStatusPending(transactionStateId) ||
    isTransactionStatusAwaitingApproval(transactionStateId)

  if (
    isPaymentRedirectTransactionCompletedStatus(statusId) ||
    (isPaymentRedirectTransactionSuccessStatus(statusId) && isTransactionInProgress)
  ) {
    return 'completed'
  }

  if (isPaymentRedirectTransactionSuccessStatus(statusId)) {
    return 'succeed'
  }

  return undefined
}

const getLatestTransactionStateId = async ({
  apiClient,
  locale,
  transactionId,
  fallbackStateId,
}: {
  apiClient: ClientApiClient
  locale: string
  transactionId: string
  fallbackStateId: number
}): Promise<number> => {
  try {
    const statusHistory = await apiClient.getTransactionStatusHistory(transactionId, locale)
    const latest = statusHistory.history.reduce((acc, item) => {
      if (!acc) {
        return item
      }

      return new Date(item.createdDate) > new Date(acc.createdDate) ? item : acc
    }, statusHistory.history[0])

    return typeof latest?.transactionState?.id === 'number'
      ? latest.transactionState.id
      : fallbackStateId
  } catch {
    return fallbackStateId
  }
}

export const DepositRedirectFromProviderPage: React.FC = () => {
  const { statusId } = useParams<{ statusId?: string }>()
  const navigate = useNavigate()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const transaction = transactionStorage().getProviderDepositResponseTransaction()

  useEffect(() => {
    if (!transaction?.id || !statusId) {
      navigate('/dashboard')
      return
    }

    let isCancelled = false

    const handleRedirect = async () => {
      const depositTransactionBasePath = getDepositTransactionBasePath(transaction)
      const fallbackTransactionStateId = transaction.data.state?.id ?? TransactionStatus.None
      const latestTransactionStateId = await getLatestTransactionStateId({
        apiClient,
        locale,
        transactionId: transaction.id,
        fallbackStateId: fallbackTransactionStateId,
      })
      if (isCancelled) {
        return
      }

      const redirectTarget = getRedirectTarget(statusId, latestTransactionStateId)

      if (depositTransactionBasePath && redirectTarget) {
        if (redirectTarget === 'succeed') {
          navigate(`${depositTransactionBasePath}/transaction/succeed`, {
            state: {
              data: {
                ...transaction.data,
                state: {
                  ...transaction.data.state,
                  id: latestTransactionStateId,
                },
              },
              providerCategoryId: transaction.providerCategoryId,
              navigateToTransactionHistory: true,
            },
          })
          return
        }

        navigate(`${depositTransactionBasePath}/transaction/${redirectTarget}`, {
          state: transaction,
        })
        return
      }

      const basePath = transaction.redirectPath ?? getDepositTransactionBasePath(transaction)

      if (basePath && redirectTarget) {
        if (redirectTarget === 'succeed') {
          navigate(`${basePath}/transaction/succeed`, {
            state: {
              data: {
                ...transaction.data,
                state: {
                  ...transaction.data.state,
                  id: latestTransactionStateId,
                },
              },
              providerCategoryId: transaction.providerCategoryId,
              navigateToTransactionHistory: true,
            },
          })
          return
        }

        navigate(`${basePath}/transaction/${redirectTarget}`, { state: transaction })
        return
      }

      navigate('/dashboard')
    }
    void handleRedirect()

    return () => {
      isCancelled = true
    }
  }, [apiClient, locale, navigate, statusId, transaction])

  return <Loading isLoading showLoadingIcon />
}

export const transactionStorage = () => ({
  getProviderDepositResponseTransaction,
  addProviderDepositResponseTransaction,
  removeProviderResponseTransactions,
})

const getProviderDepositResponseTransaction = (): DepositRedirectStorageData | undefined => {
  const fromSessionStorage = safeSessionStorage.getJSON<DepositRedirectStorageData>(
    PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY
  )
  if (isDepositRedirectStorageData(fromSessionStorage)) {
    return fromSessionStorage
  }

  const fromLocalStorage = safeLocalStorage.getJSON<DepositRedirectStorageData>(
    PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY
  )
  if (isDepositRedirectStorageData(fromLocalStorage)) {
    return fromLocalStorage
  }

  return undefined
}

const addProviderDepositResponseTransaction = (data: {
  transaction: MasterTransaction
  values: DepositFormValues
  match: Readonly<Params>
  redirectPath?: string
}) => {
  removeProviderResponseTransactions()
  const value = createDepositRedirectStorageData(data)

  safeSessionStorage.setJSON(PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY, value)
  safeLocalStorage.setJSON(PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY, value)
}

const removeProviderResponseTransactions = () => {
  safeSessionStorage.removeItem(PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY)
  safeLocalStorage.removeItem(PROVIDER_DEPOSIT_RESPONSE_TRANSACTION_KEY)
}

const createDepositRedirectStorageData = (data: {
  transaction: MasterTransaction
  values: DepositFormValues
  match: Readonly<Params>
  redirectPath?: string
}): DepositRedirectStorageData => {
  const { transaction, values, match, redirectPath } = data

  return {
    id: transaction.id,
    data: transaction,
    providerCategoryId: values.paymentProvider.providerCategory?.id ?? PaymentProviderType.None,
    match: {
      walletId: match.walletId,
      tradingAccountId: match.tradingAccountId,
    },
    redirectPath,
    dashboardContext: (values as any).dashboardContext,
  }
}
